import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  type ReactNode,
} from "react";
import type { CartItem, CartTotals, Product } from "@/types";

interface CartContextValue {
  items: CartItem[];
  add: (
    product: Product,
    quantity?: number,
    variant?: string | null,
    customization?: Record<string, unknown> | null,
  ) => void;
  remove: (product_id: string, variant?: string | null, customizationKey?: string | null) => void;
  update: (product_id: string, quantity: number, variant?: string | null, customizationKey?: string | null) => void;
  clear: () => void;
  totals: CartTotals;
}

const CartCtx = createContext<CartContextValue | null>(null);
const STORAGE_KEY = "pf-cart-v2";

interface CartProviderProps {
  children: ReactNode;
}

/** Stable key so customized lines do not merge with plain variants. */
function customizationKey(customization?: Record<string, unknown> | null): string {
  if (!customization || Object.keys(customization).length === 0) return "";
  try {
    return JSON.stringify(customization, Object.keys(customization).sort());
  } catch {
    return "";
  }
}

function sameLine(
  a: CartItem,
  productId: string,
  variant: string | null,
  custKey: string,
): boolean {
  return (
    a.product_id === productId &&
    a.variant === variant &&
    customizationKey(a.customization || null) === custKey
  );
}

export function CartProvider({ children }: CartProviderProps) {
  const [items, setItems] = useState<CartItem[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || localStorage.getItem("pf-cart-v1") || "[]") as CartItem[];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const add = (
    product: Product,
    quantity = 1,
    variant: string | null = null,
    customization: Record<string, unknown> | null = null,
  ): void => {
    const cust = customization && Object.keys(customization).length ? customization : null;
    const cKey = customizationKey(cust);
    setItems((prev) => {
      const idx = prev.findIndex((x) => sameLine(x, product.id, variant, cKey));
      if (idx >= 0 && !cKey) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + quantity };
        return copy;
      }
      return [
        ...prev,
        {
          product_id: product.id,
          name: product.name,
          slug: product.slug?.trim() || product.id,
          image: (product.images || [])[0],
          price: product.discount_price || product.price,
          quantity,
          variant,
          material: product.material,
          customization: cust || undefined,
        },
      ];
    });
  };

  const remove = (product_id: string, variant: string | null = null, custKey: string | null = ""): void => {
    const key = custKey ?? "";
    setItems((prev) => prev.filter((x) => !sameLine(x, product_id, variant, key)));
  };

  const update = (
    product_id: string,
    quantity: number,
    variant: string | null = null,
    custKey: string | null = "",
  ): void => {
    const key = custKey ?? "";
    setItems((prev) =>
      prev.map((x) =>
        sameLine(x, product_id, variant, key)
          ? { ...x, quantity: Math.max(1, quantity) }
          : x,
      ),
    );
  };

  const clear = (): void => setItems([]);

  const totals = useMemo((): CartTotals => {
    const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
    const shipping = 0;
    const gst = 0;
    return {
      subtotal: Math.round(subtotal * 100) / 100,
      shipping,
      gst,
      total: Math.round((subtotal - 0) * 100) / 100,
      count: items.reduce((s, i) => s + i.quantity, 0),
    };
  }, [items]);

  return (
    <CartCtx.Provider value={{ items, add, remove, update, clear, totals }}>
      {children}
    </CartCtx.Provider>
  );
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartCtx);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
